import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Alert,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

const NewVersionModal = ({ open, onClose, template, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");

  // 모달 닫기
  const handleClose = () => {
    if (loading) return;
    setSelectedFile(null);
    setError("");
    onClose();
  };

  // 파일 선택
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    validateAndSetFile(file);
  };

  // 파일 유효성 검사 및 설정
  const validateAndSetFile = (file) => {
    setError("");

    if (!file) return;

    if (file.type !== "application/pdf") {
      setError("PDF 파일만 업로드 가능합니다.");
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      // 50MB 제한
      setError("파일 크기는 50MB 이하만 가능합니다.");
      return;
    }

    setSelectedFile(file);
  };

  // 파일 삭제
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError("");
  };

  // 드래그 앤 드롭 핸들러
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    validateAndSetFile(file);
  };

  // 새 버전 생성
  const handleCreateNewVersion = async () => {
    console.log("handleCreateNewVersion 호출됨");
    console.log("selectedFile:", selectedFile);
    console.log("template:", template);

    if (!selectedFile || !template) {
      console.log("early return - selectedFile 또는 template이 없음");
      return;
    }

    try {
      setLoading(true);
      setError("");
      console.log("API 호출 시작...");

      // 1. PDF 업로드
      const formData = new FormData();
      formData.append("file", selectedFile);

      const uploadResponse = await fetch(
        "https://contract.tirebank.kr/api/contract-pdf/upload",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error("PDF 업로드에 실패했습니다.");
      }

      const newPdfId = await uploadResponse.text();
      console.log("업로드 완료, newPdfId:", newPdfId);

      // 2. 새 버전 생성
      console.log("새 버전 생성 API 호출...");
      const newVersionResponse = await fetch(
        `https://contract.tirebank.kr/api/contract-pdf/templates/${
          template.id
        }/new-version?newOriginalPdfId=${encodeURIComponent(newPdfId)}`,
        { method: "POST" }
      );

      if (!newVersionResponse.ok) {
        throw new Error("새 버전 생성에 실패했습니다.");
      }

      const newTemplate = await newVersionResponse.json();

      // 성공
      alert(`새 버전(v${newTemplate.version})이 성공적으로 생성되었습니다.`);
      handleClose();

      if (onSuccess) {
        onSuccess(newTemplate);
      }
    } catch (err) {
      console.error("새 버전 생성 중 오류:", err);
      setError(err.message || "새 버전 생성에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 파일 크기 포맷팅
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      {/* 헤더 */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          pb: 1,
          borderBottom: "1px solid #EEEEEE",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            새 버전 생성
          </Typography>
          {template && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {template.templateName} (현재 v{template.version})
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          size="small"
          sx={{ mt: -0.5, mr: -1 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* 컨텐츠 */}
      <DialogContent sx={{ pt: 3 }}>
        {/* 안내 문구 */}
        <Alert severity="info" sx={{ mb: 3, mt: 3 }}>
          새 PDF 파일을 업로드하면 <br />
          기존 서명 항목 구성(텍스트, 서명,체크박스등)이 자동으로 복사됩니다.
        </Alert>

        {/* 에러 메시지 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 파일 업로드 영역 */}
        {!selectedFile ? (
          <Box
            component="label"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              border: "2px dashed",
              borderColor: isDragging ? "#1976d2" : "#E0E0E0",
              borderRadius: 2,
              p: 5,
              backgroundColor: isDragging ? "#E3F2FD" : "#FAFAFA",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#1976d2",
                backgroundColor: "#F5F5F5",
              },
            }}
          >
            <input
              type="file"
              accept="application/pdf"
              hidden
              onChange={handleFileSelect}
            />
            <CloudUploadIcon
              sx={{
                fontSize: 56,
                color: isDragging ? "#1976d2" : "#BDBDBD",
                mb: 2,
              }}
            />
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
              PDF 파일을 여기에 드래그하세요
            </Typography>
            <Typography variant="body2" color="text.secondary">
              또는 클릭하여 파일 선택
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              최대 50MB
            </Typography>
          </Box>
        ) : (
          // 선택된 파일 표시
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 2,
              border: "1px solid #E0E0E0",
              borderRadius: 2,
              backgroundColor: "#FAFAFA",
            }}
          >
            <FileIcon sx={{ fontSize: 40, color: "#D32F2F", mr: 2 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedFile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatFileSize(selectedFile.size)}
              </Typography>
            </Box>
            <IconButton
              onClick={handleRemoveFile}
              disabled={loading}
              size="small"
              sx={{ ml: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </DialogContent>

      {/* 액션 버튼 */}
      <DialogActions sx={{ p: 2, pt: 1, borderTop: "1px solid #EEEEEE" }}>
        <Button onClick={handleClose} disabled={loading} sx={{ color: "#666" }}>
          취소
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateNewVersion}
          disabled={!selectedFile || loading}
          sx={{
            minWidth: 120,
            backgroundColor: "#1976d2",
            "&:hover": { backgroundColor: "#1565c0" },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} sx={{ color: "white" }} />
              <span>생성 중...</span>
            </Box>
          ) : (
            "새 버전 생성"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewVersionModal;
